import { useState, useEffect, useCallback } from 'react'
import { api } from '../lib/api'
import { useAuth } from '../hooks/useAuth'
import toast from 'react-hot-toast'
import { Modal, Input, Textarea, Select, Empty, LinkCell, HtmlCell } from '../components/UI'
import ContentGenerator from '../components/ContentGenerator'
import { ViewProduct } from '../components/ViewModal'
import { useColumns, ColumnPicker } from '../components/ColumnPicker'
import { DensityToggle } from '../components/DensityToggle'
import { useTableFilter, FilterButton } from '../components/TableFilter'
import { useResizableColumns } from '../hooks/useResizableColumns'
import { useRowSelection } from '../hooks/useRowSelection'

const fmt = v => v == null ? '—' : `€${Number(v).toFixed(2)}`
const fmtRoas = v => (!v || v === 0) ? '—' : `${Number(v).toFixed(2)}x`

function HtmlField({ label, hasExisting, onChange }) {
  const [uploaded, setUploaded] = useState(null)  // null = untouched; string = new content (or '' = cleared)
  const onFile = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => { const t = String(reader.result || ''); setUploaded(t); onChange(t) }
    reader.readAsText(file)
    e.target.value = ''
  }
  const clear = () => { setUploaded(''); onChange('') }
  // State shown to the user: a freshly uploaded file, an existing one, or none.
  const hasFile = uploaded ? uploaded.length > 0 : (uploaded === '' ? false : hasExisting)
  return (
    <div>
      <label style={{fontSize:10.5,fontWeight:600,color:'var(--text2)',textTransform:'uppercase',letterSpacing:'0.6px',fontFamily:'DM Mono,monospace',display:'block',marginBottom:5}}>{label}</label>
      <div style={{display:'flex',gap:8,alignItems:'center'}}>
        <label style={{fontSize:11,fontWeight:600,padding:'5px 12px',background:'var(--bg3)',border:'1px dashed var(--border2)',borderRadius:6,color:'var(--accent)',cursor:'pointer'}}>
          {hasFile ? '⤴ Replace .html' : '⬆ Upload .html'}
          <input type="file" accept=".html,.htm,text/html" onChange={onFile} style={{display:'none'}} />
        </label>
        {hasFile
          ? <span style={{fontSize:11,color:'#10B981',fontWeight:600}}>✓ {uploaded ? 'new file ready' : 'file on record'}</span>
          : <span style={{fontSize:11,color:'var(--text3)'}}>no file</span>}
        {hasFile && <button type="button" onClick={clear} style={{fontSize:10,padding:'2px 8px',background:'none',border:'1px solid var(--border2)',borderRadius:6,color:'#EF4444',cursor:'pointer'}}>remove</button>}
      </div>
    </div>
  )
}
const ALL_COLS = [

  { key:'product_id',                  label:'ID' },
  { key:'product_name',                label:'Comp Product Name' },
  { key:'sourcing_cost_usd',           label:'Src USD' },
  { key:'sourcing_cost_eur',           label:'Src €' },
  { key:'shipping_cost_usd',           label:'Ship USD' },
  { key:'shipping_cost_eur',           label:'Ship €' },
  { key:'total_cogs',                  label:'COGs €' },
  { key:'selling_price_eur',           label:'Target Selling Price' },
  { key:'shopify_fee',                 label:'Shopify Fee' },
  { key:'currency_conv',               label:'Curr Conv' },
  { key:'total_fees',                  label:'Total Fees' },
  { key:'profit',                      label:'Profit €' },
  { key:'be_roas',                     label:'BE ROAS' },
  { key:'profit_margin',               label:'Margin %' },
  { key:'aliexpress_link',             label:'Sourcing Link' },
  { key:'competitor_product_url',      label:'Comp URL' },
  { key:'competitor_advertorial_url',   label:'Advertorial URL' },
  { key:'competitor_page_ad_url',      label:'Comp Page Ad' },
  { key:'comp_adv_html',               label:'Comp Adv HTML' },
  { key:'comp_sales_html',             label:'Comp Sales HTML' },
  { key:'competitor_winning_ad_links', label:'Winning Ad Links' },
  { key:'competitor_ad_copy_primary',  label:'Comp Primary' },
  { key:'competitor_ad_copy_headline', label:'Comp Headline' },
  { key:'competitor_ad_creative_link', label:'Comp Creative' },
  { key:'offer_to_use',                label:'Offers (per market)' },
  { key:'research_notes',              label:'Your Recommendations' },
  { key:'platform_found_on', label:'Platform Found On' },
  { key:'competitor_price', label:'Competitor Price' },
  { key:'ad_running_duration', label:'Ad Running Duration' },
  { key:'ad_countries', label:'Countries Running In' },
  { key:'scale_signal', label:'Scale Signal' },
  { key:'ad_hook', label:'Ad Hook' },
  { key:'ad_angle', label:'Angle Used' },
  { key:'market_saturated', label:'Market Saturated?' },
  { key:'desire_targeted', label:'Desire Targeted' },
  { key:'customer_concerns', label:'Customer Concerns' },
  { key:'problem_addressed', label:'Problem Addressed' },
  { key:'target_audience', label:'Target Audience' },
  { key:'competitor_positioning', label:'Key Competitor Positioning' },
  { key:'work_status', label:'Work Status' },
  { key:'approval', label:'Approval' },
  { key:'approved_by', label:'Approved By' },
  { key:'review_notes', label:'Review Notes' },
]

function Cell({ col, row, canEditRow, onHtmlChanged }) {
  switch(col) {
    case 'product_id':                  return <strong>{row.product_id}</strong>
    case 'product_name':                return <strong style={{whiteSpace:'normal',wordBreak:'break-word',display:'block',width:'100%'}}>{row.product_name}</strong>
    case 'aliexpress_link':             return row.aliexpress_link ? <a href={row.aliexpress_link} target="_blank" rel="noreferrer" onClick={e=>e.stopPropagation()} style={{color:'var(--accent)'}}>Link ↗</a> : <span style={{color:'var(--text3)'}}>—</span>
    case 'sourcing_cost_usd':           return <span className="td-num">${row.sourcing_cost_usd}</span>
    case 'sourcing_cost_eur':           return <span className="td-num">{fmt(row.sourcing_cost_eur)}</span>
    case 'shipping_cost_usd':           return <span className="td-num">${row.shipping_cost_usd}</span>
    case 'shipping_cost_eur':           return <span className="td-num">{fmt(row.shipping_cost_eur)}</span>
    case 'total_cogs':                  return <span className="td-num">{fmt(row.total_cogs)}</span>
    case 'selling_price_eur':           return <span className="td-num">{fmt(row.selling_price_eur)}</span>
    case 'shopify_fee':                 return <span className="td-num">{fmt(row.shopify_fee)}</span>
    case 'currency_conv':               return <span className="td-num">{fmt(row.currency_conv)}</span>
    case 'total_fees':                  return <span className="td-num">{fmt(row.total_fees)}</span>
    case 'profit':                      return <span className={`td-num ${row.profit>=0?'td-profit-pos':'td-profit-neg'}`}>{fmt(row.profit)}</span>
    case 'be_roas':                     return <span className="td-roas">{fmtRoas(row.be_roas)}</span>
    case 'profit_margin': {
      const m = parseFloat(row.profit_margin)
      const col = isNaN(m) ? 'var(--text3)' : m>=60 ? 'var(--accent3)' : m>0 ? '#F59E0B' : '#EF4444'
      return <span className="td-num" style={{color:col,fontWeight:700}}>{isNaN(m)?'—':m+'%'}</span>
    }
    case 'competitor_product_url':      return <LinkCell url={row.competitor_product_url} />
    case 'competitor_advertorial_url':   return <LinkCell url={row.competitor_advertorial_url} />
    case 'competitor_page_ad_url':      return <LinkCell url={row.competitor_page_ad_url} />
    case 'comp_adv_html':               return <HtmlCell has={row.has_adv_html} id={row.id} which="adv" canEdit={canEditRow} onChanged={onHtmlChanged} />
    case 'comp_sales_html':             return <HtmlCell has={row.has_sales_html} id={row.id} which="sales" canEdit={canEditRow} onChanged={onHtmlChanged} />
    case 'competitor_winning_ad_links': {
      const links = (row.competitor_winning_ad_links || '').split('\n').map(l => l.trim().replace(/^"|"$/g, '')).filter(Boolean)
      if (!links.length) return <span>—</span>
      return <div style={{display:'flex',flexDirection:'column',gap:3}}>
        {links.map((url, i) => (
          url.startsWith('http') 
            ? <a key={i} href={url} target="_blank" rel="noopener noreferrer" onClick={e=>e.stopPropagation()} style={{color:'#1E6FBF',fontSize:11,textDecoration:'none',display:'block'}} onMouseEnter={e=>e.target.style.textDecoration='underline'} onMouseLeave={e=>e.target.style.textDecoration='none'}>🔗 Ad {i+1}</a>
            : <span key={i} style={{fontSize:11,color:'var(--text2)'}}>{url}</span>
        ))}
      </div>
    }
    case 'competitor_ad_copy_primary':  return <span className="td-wrap" style={{width:'100%',display:'block'}}>{row.competitor_ad_copy_primary||'—'}</span>
    case 'competitor_ad_copy_headline': return <span>{row.competitor_ad_copy_headline||'—'}</span>
    case 'competitor_ad_creative_link': return <LinkCell url={row.competitor_ad_creative_link} />
    case 'offer_to_use': {
      const obm = row.offer_by_market || {}
      const entries = Object.entries(obm).filter(([,v]) => v && v.trim())
      if (!entries.length) return <span>—</span>
      return <span className="td-wrap" style={{width:'100%',display:'block'}}>{entries.map(([m,v]) => <span key={m} style={{display:'block',fontSize:11}}><strong>{m}:</strong> {v}</span>)}</span>
    }
    case 'research_notes':              return <span className="td-wrap" style={{width:'100%',display:'block'}}>{row.research_notes||'—'}</span>
    case 'platform_found_on':                return <span className="td-wrap" style={{width:'100%',display:'block'}}>{row.platform_found_on||'—'}</span>
    case 'competitor_price':                return <span className="td-wrap" style={{width:'100%',display:'block'}}>{row.competitor_price||'—'}</span>
    case 'ad_running_duration':                return <span className="td-wrap" style={{width:'100%',display:'block'}}>{row.ad_running_duration||'—'}</span>
    case 'ad_countries':                return <span className="td-wrap" style={{width:'100%',display:'block'}}>{row.ad_countries||'—'}</span>
    case 'scale_signal':                return <span className="td-wrap" style={{width:'100%',display:'block'}}>{row.scale_signal||'—'}</span>
    case 'ad_hook':                return <span className="td-wrap" style={{width:'100%',display:'block'}}>{row.ad_hook||'—'}</span>
    case 'ad_angle':                return <span className="td-wrap" style={{width:'100%',display:'block'}}>{row.ad_angle||'—'}</span>
    case 'market_saturated':                return <span className="td-wrap" style={{width:'100%',display:'block'}}>{row.market_saturated||'—'}</span>
    case 'desire_targeted':                return <span className="td-wrap" style={{width:'100%',display:'block'}}>{row.desire_targeted||'—'}</span>
    case 'customer_concerns':                return <span className="td-wrap" style={{width:'100%',display:'block'}}>{row.customer_concerns||'—'}</span>
    case 'problem_addressed':                return <span className="td-wrap" style={{width:'100%',display:'block'}}>{row.problem_addressed||'—'}</span>
    case 'target_audience':                return <span className="td-wrap" style={{width:'100%',display:'block'}}>{row.target_audience||'—'}</span>
    case 'competitor_positioning':                return <span className="td-wrap" style={{width:'100%',display:'block'}}>{row.competitor_positioning||'—'}</span>
    case 'approved_by': return (row.approved_by && row.approved_by !== '(pre-existing)')
      ? <span style={{fontSize:11,color:'var(--text2)'}}>{row.approved_by}{row.approved_at ? <span style={{color:'var(--text3)',display:'block',fontSize:10}}>{new Date(row.approved_at).toLocaleDateString()}</span> : null}</span>
      : <span style={{color:'var(--text3)'}}>—</span>
    case 'review_notes': return <span className="td-wrap" style={{width:'100%',display:'block'}}>{row.review_notes||'—'}</span>
    default: return <span>—</span>
  }
}

export function ProductResearch() {
  const { user } = useAuth()
  const isOwner = user?.role === 'owner'
  const PR_FILTER_GROUPS = [
    { key:'work_status', label:'Work Status', options:[
      {value:'In Progress',label:'In Progress'},{value:'Ready',label:'Ready'},
      {value:'Updates Required',label:'Updates Required'},{value:'Cancelled',label:'Cancelled'}] },
    { key:'approval', label:'Approval', options:[
      {value:'Pending',label:'Pending'},{value:'Approved',label:'Approved'},
      {value:'Updates Needed',label:'Updates Needed'},{value:'Rejected',label:'Rejected'}] },
  ]
  const { filter, toggle, clearAll, matches, activeCount } = useTableFilter('products', PR_FILTER_GROUPS)
  const canApp = isOwner || ((typeof user?.permissions === 'object' && user?.permissions?.can_approve) === true)
  const _prPerm = (typeof user?.permissions === 'object' && user?.permissions?.products) || {}
  const canEdit = isOwner || _prPerm === true || _prPerm.edit === true
  const [reviewPrompt, setReviewPrompt] = useState(null)
  const [approvePrompt, setApprovePrompt] = useState(null)
  const [approveMarkets, setApproveMarkets] = useState([])
  const [reviewText, setReviewText] = useState('')
  const [cleanupPrompt, setCleanupPrompt] = useState(null)  // { row, sections }
  const [cleaning, setCleaning] = useState('')
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(null)
  const [viewRow, setViewRow] = useState(null)
  const [form, setForm] = useState({})
  const [saving, setSaving] = useState(false)
  const [generatorProduct, setGeneratorProduct] = useState(null)
  const [activeMarkets, setActiveMarkets] = useState(['SE','NL-BE','UK','US','AU','NO','GE','FR','DK','FI','CA'])
  const [offersOpen, setOffersOpen] = useState(false)
  useEffect(() => { api.getSettings().then(st => { if (st.active_markets) setActiveMarkets(st.active_markets) }).catch(()=>{}) }, [])
  const { cols: colVis, isVisible, toggle: colToggle, reset: colReset } = useColumns('products')
  const { getThProps, getTdProps, Resizer } = useResizableColumns('products')
  const visibleCols = ALL_COLS.filter(c => isVisible(c.key))

  const { rowProps } = useRowSelection('products')
  const load = useCallback(async () => {
    setLoading(true)
    try { setRows(await api.getProducts()) } catch (err) { toast.error(err.message) }
    setLoading(false)
  }, [])
  // Refresh without unmounting the table, preserving scroll position — used for
  // approval / work-status changes so you don't lose the row you're working on.
  const softLoad = useCallback(async () => {
    const wrap = document.querySelector('.table-wrap')
    const top = wrap ? wrap.scrollTop : 0
    const left = wrap ? wrap.scrollLeft : 0
    try {
      const fresh = await api.getProducts()
      setRows(fresh)
      requestAnimationFrame(() => requestAnimationFrame(() => {
        const w = document.querySelector('.table-wrap')
        if (w) { w.scrollTop = top; w.scrollLeft = left }
      }))
    } catch (err) { toast.error(err.message) }
  }, [])

  useEffect(() => { load() }, [load])

  function openAdd() {
    const nextId = rows.length > 0
      ? Math.max(...rows.map(r => parseInt(r.product_id) || 0)) + 1
      : 101
    setForm({ product_id: nextId, usd_eur_rate: 0.93, work_status: 'In Progress' })
    setModal('add')
  }
  function openEdit(r) { setForm(r); setModal(r) }

  async function save() {
    setSaving(true)
    try {
      if (modal === 'add') await api.createProduct(form)
      else await api.updateProduct(modal.id, form)
      toast.success(modal === 'add' ? 'Product added!' : 'Saved!')
      setModal(null); softLoad()
    } catch (err) { toast.error(err.message) }
    setSaving(false)
  }

  async function del(id) {
    if (!window.confirm('Delete this product and all related records?')) return
    try { await api.deleteProduct(id); toast.success('Deleted'); softLoad() }
    catch (err) { toast.error(err.message) }
  }

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }))

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%'}}>

      <div className="toolbar">
        <div className="toolbar-left">
          <span className="toolbar-title">📦 Product Research</span>
          <span className="toolbar-sub">{rows.length} products</span>
        </div>
        <div style={{display:'flex',gap:8}}>
          <FilterButton groups={PR_FILTER_GROUPS} filter={filter} toggle={toggle} clearAll={clearAll} activeCount={activeCount} />
          <DensityToggle />
          <ColumnPicker section="products" cols={colVis} toggle={colToggle} reset={colReset} />
          <button className="btn btn-primary" onClick={openAdd}>+ Add Product</button>
        </div>
      </div>
      <div className="table-wrap" style={{flex:1,overflow:'auto'}}>
        {loading ? <Empty icon="⏳" title="Loading..." /> : rows.length === 0
          ? <Empty icon="📦" title="No products yet" sub="Click Add Product to get started" />
          : <table className="data-table">
              <thead><tr>
                {visibleCols.map((c, ci) => <th key={c.key} {...getThProps(c.key, ci, visibleCols)}>{c.label}<Resizer colKey={c.key} index={ci}/></th>)}
                <th style={{width:170,textAlign:'center',position:'sticky',right:0,zIndex:6,background:'var(--bg3)',borderLeft:'2px solid var(--border2)'}}>Actions</th>
              </tr></thead>
              <tbody>{rows.filter(r => matches(r, {})).map((r, ri) => (
                <tr key={r.id} {...rowProps(r.id)}>
                  {visibleCols.map((c, ci) => <td key={c.key} {...getTdProps(c.key, ci, visibleCols, ri)}>{c.key === 'work_status'
                    ? (() => {
                        const wsColor = { 'Ready':'var(--accent3)', 'Updates Required':'#F59E0B', 'On Hold':'var(--text3)', 'Cancelled':'var(--text3)', 'In Progress':'var(--text)' }
                        return <select value={r.work_status||'In Progress'}
                          onClick={e=>e.stopPropagation()}
                          onChange={async e => { try { await api.updateProduct(r.id, { work_status: e.target.value }); toast.success(e.target.value === 'Ready' ? 'Marked Ready — sent for review' : 'Updated'); softLoad() } catch(err){ toast.error(err.message) } }}
                          style={{background:'var(--bg3)',border:'1px solid var(--border2)',borderRadius:6,color:wsColor[r.work_status]||'var(--text)',padding:'4px 8px',fontSize:11,fontWeight:600,cursor:'pointer',outline:'none',width:'100%'}}>
                          {['In Progress','Ready','Updates Required','On Hold','Cancelled'].map(st=><option key={st} value={st}>{st}</option>)}
                        </select>
                      })()
                    : c.key === 'approval'
                    ? (() => {
                        const colorMap = { 'Approved':'var(--accent3)', 'Rejected':'#EF4444', 'Updates Needed':'#F59E0B', 'On Hold':'var(--text3)', 'Pending':'var(--text3)' }
                        if (!canApp) return <span style={{fontSize:11,fontWeight:700,color:colorMap[r.approval]||'var(--text3)'}}>{r.approval||'—'}</span>
                        return <select value={r.approval||''}
                          onClick={e=>e.stopPropagation()}
                          onChange={async e => { const val = e.target.value; if (['Updates Needed','Rejected','On Hold'].includes(val)) { setReviewText(r.review_notes||''); setReviewPrompt({ row:r, status:val }); return } if (val === 'Approved') { setApproveMarkets([...activeMarkets]); setApprovePrompt(r); return } try { await api.updateProduct(r.id, { approval: val }); toast.success('Updated'); softLoad() } catch(err){ toast.error(err.message) } }}
                          style={{background:'var(--bg3)',border:'1px solid var(--border2)',borderRadius:6,color:colorMap[r.approval]||'var(--text)',padding:'4px 8px',fontSize:11,fontWeight:700,cursor:'pointer',outline:'none',width:'100%'}}>
                          <option value="">— —</option>
                          {['Pending','Approved','Updates Needed','Rejected','On Hold'].map(st=><option key={st} value={st}>{st}</option>)}
                        </select>
                      })()
                    : <Cell col={c.key} row={r} canEditRow={canEdit} onHtmlChanged={softLoad} />}</td>)}
                  <td style={{position:'sticky',right:0,zIndex:2,background:'var(--bg2)',borderLeft:'2px solid var(--border2)',padding:'6px 8px'}}>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:4,width:148}}>
                      <button className="btn btn-secondary" style={{padding:'4px 6px',fontSize:11,whiteSpace:'nowrap',justifyContent:'center'}} onClick={()=>setViewRow(r)}>👁 View</button>
                      <button className="btn btn-secondary" style={{padding:'4px 6px',fontSize:11,whiteSpace:'nowrap',justifyContent:'center'}} onClick={()=>openEdit(r)}>✏️ Edit</button>
                      <button className="btn btn-danger" style={{padding:'4px 6px',fontSize:11,whiteSpace:'nowrap',justifyContent:'center'}} onClick={()=>del(r.id)}>🗑 Del</button>
                      <button style={{background:'#7C3AED',color:'#fff',border:'none',padding:'4px 6px',borderRadius:6,fontSize:11,fontWeight:600,cursor:'pointer',whiteSpace:'nowrap',display:'flex',alignItems:'center',justifyContent:'center'}} onClick={()=>setGeneratorProduct(r)}>✨ Gen</button>
                    </div>
                  </td>
                </tr>
              ))}</tbody>
            </table>}
      </div>

      <ViewProduct row={viewRow} onClose={() => setViewRow(null)} />
      {reviewPrompt && <Modal title={`Set Approval: ${reviewPrompt.status}`} onClose={() => { setReviewPrompt(null); setReviewText('') }}
        footer={<><button className="btn btn-ghost" onClick={() => { setReviewPrompt(null); setReviewText('') }}>Cancel</button>
          <button className="btn btn-primary" disabled={!reviewText.trim()} onClick={async () => {
            try {
              const row = reviewPrompt.row, status = reviewPrompt.status
              await api.updateProduct(row.id, { approval: status, review_notes: reviewText.trim() })
              setReviewPrompt(null); setReviewText(''); toast.success('Updated'); softLoad()
              // Offer to clean up downstream rows when a product is being stopped
              if (['Rejected','On Hold','Cancelled'].includes(status)) {
                try {
                  const d = await api.productDownstream(row.product_id)
                  const any = (d.sections||[]).some(x => x.total > 0)
                  if (any) setCleanupPrompt({ row, sections: d.sections })
                } catch {}
              }
            }
            catch(err){ toast.error(err.message) }
          }}>Save</button></>}>
        <div style={{fontSize:13,color:'var(--text2)',marginBottom:12}}>Explain the reason. This note will be recorded for the researcher.</div>
        <textarea value={reviewText} onChange={e => setReviewText(e.target.value)} placeholder="Reason / what needs to change..." autoFocus
          style={{width:'100%',minHeight:120,padding:'9px 12px',border:'1px solid var(--border2)',borderRadius:8,background:'var(--bg3)',color:'var(--text)',fontSize:13,fontFamily:'DM Sans,sans-serif'}} />
      </Modal>}
      {cleanupPrompt && (() => {
        const secs = cleanupPrompt.sections || []
        const sel = cleanupPrompt.selected || {}
        const expanded = cleanupPrompt.expanded || {}
        const setPrompt = (patch) => setCleanupPrompt(cp => ({ ...cp, ...patch }))
        const isSel = (t, id) => (sel[t] || []).includes(id)
        const toggleRow = (t, id) => {
          const cur = sel[t] || []
          const next = cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id]
          setPrompt({ selected: { ...sel, [t]: next } })
        }
        const toggleSection = (sec) => {
          const selectable = sec.rows.filter(r => !r.locked).map(r => r.id)
          const cur = sel[sec.table] || []
          const allOn = selectable.length > 0 && selectable.every(id => cur.includes(id))
          setPrompt({ selected: { ...sel, [sec.table]: allOn ? [] : selectable } })
        }
        const totalSelected = Object.values(sel).reduce((a, b) => a + (b ? b.length : 0), 0)
        const workSelected = secs.reduce((a, sec) =>
          a + sec.rows.filter(r => (sel[sec.table] || []).includes(r.id) && r.hasWork).length, 0)
        const doDelete = async () => {
          if (!window.confirm(`Delete ${totalSelected} selected row(s)?` + (workSelected ? `\n\n${workSelected} of them contain work that will be permanently lost.` : ''))) return
          setCleaning('sel')
          try {
            const out = await api.productCleanup(cleanupPrompt.row.product_id, { selection: sel })
            const n = Object.values(out.deleted || {}).reduce((a, b) => a + b, 0)
            toast.success(n ? `Deleted ${n} row(s)` : 'Nothing deleted')
            setCleanupPrompt(null)
          } catch (err) { toast.error(err.message) }
          setCleaning('')
        }
        return (
        <Modal title={`Clean up: ${cleanupPrompt.row.product_id} — ${cleanupPrompt.row.product_name || 'Product'}`} onClose={() => setCleanupPrompt(null)}
          footer={<>
            <span style={{flex:1,fontSize:12,color:'var(--text3)',fontFamily:'DM Mono,monospace'}}>
              {totalSelected} selected{workSelected ? ` · ${workSelected} with work` : ''}
            </span>
            <button className="btn btn-ghost" onClick={() => setCleanupPrompt(null)}>Cancel</button>
            <button className="btn btn-danger" disabled={!totalSelected || !!cleaning} onClick={doDelete}>
              {cleaning === 'sel' ? 'Deleting…' : `Delete ${totalSelected || ''} selected`}
            </button>
          </>}>
          <div style={{fontSize:13,color:'var(--text2)',marginBottom:14}}>
            Tick the rows you want to remove. Rows containing work are marked — active campaigns can't be deleted here.
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:8}}>
            {secs.filter(sec => sec.total > 0).map(sec => {
              const open = !!expanded[sec.table]
              const selCount = (sel[sec.table] || []).length
              return (
                <div key={sec.table} style={{border:'1px solid var(--border2)',borderRadius:8,overflow:'hidden'}}>
                  <div style={{display:'flex',alignItems:'center',gap:10,padding:'9px 12px',background:'var(--bg3)',cursor:'pointer'}}
                    onClick={() => setPrompt({ expanded: { ...expanded, [sec.table]: !open } })}>
                    <span style={{fontSize:12,fontWeight:700,color:'var(--text)',flex:1,whiteSpace:'nowrap'}}>{open ? '▾' : '▸'} {sec.label}</span>
                    <span style={{fontSize:10,fontFamily:'DM Mono,monospace',color:'var(--text3)',whiteSpace:'nowrap'}}>
                      {sec.total}{sec.withWork > 0 && <span style={{color:'#F59E0B'}}> · {sec.withWork} work</span>}
                      {selCount > 0 && <span style={{color:'var(--accent3)'}}> · {selCount} ✓</span>}
                    </span>
                    <button className="btn btn-ghost" style={{fontSize:9,padding:'2px 7px',whiteSpace:'nowrap',minWidth:0}}
                      onClick={e => { e.stopPropagation(); toggleSection(sec) }}>all</button>
                  </div>
                  {open && <div style={{padding:'6px 12px',display:'flex',flexDirection:'column',gap:3,background:'var(--bg2)'}}>
                    {sec.rows.map(r => (
                      <div key={r.id}
                        onClick={() => { if (!r.locked) toggleRow(sec.table, r.id) }}
                        style={{display:'grid',gridTemplateColumns:'18px 56px 1fr auto',alignItems:'center',gap:10,
                          padding:'6px 4px',fontSize:12,borderRadius:6,
                          cursor:r.locked?'not-allowed':'pointer',opacity:r.locked?0.45:1,
                          background: isSel(sec.table, r.id) ? 'rgba(59,130,246,0.10)' : 'transparent'}}>
                        <input type="checkbox" disabled={r.locked} checked={isSel(sec.table, r.id)}
                          onChange={() => toggleRow(sec.table, r.id)} onClick={e => e.stopPropagation()}
                          style={{width:14,height:14,margin:0,padding:0,cursor:r.locked?'not-allowed':'pointer'}} />
                        <span style={{fontFamily:'DM Mono,monospace',fontWeight:700,color:'var(--text)',fontSize:11}}>{r.market}</span>
                        <span style={{color:'var(--text3)',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{r.status || '—'}</span>
                        <span style={{display:'flex',gap:6,alignItems:'center',whiteSpace:'nowrap'}}>
                          {r.hasWork && <span style={{fontSize:9,color:'#F59E0B',fontWeight:700,letterSpacing:'0.4px'}}>● WORK</span>}
                          {r.locked && <span style={{fontSize:9,color:'#EF4444',fontWeight:700,letterSpacing:'0.4px'}}>ACTIVE</span>}
                        </span>
                      </div>
                    ))}
                  </div>}
                </div>
              )
            })}
          </div>
        </Modal>)
      })()}
      {approvePrompt && <Modal title={`Approve: ${approvePrompt.product_name||('Product '+approvePrompt.product_id)}`} onClose={() => setApprovePrompt(null)}
        footer={<><button className="btn btn-ghost" onClick={() => setApprovePrompt(null)}>Cancel</button>
          <button className="btn btn-primary" disabled={!approveMarkets.length} onClick={async () => {
            try {
              const upd = await api.updateProduct(approvePrompt.id, { approval: 'Approved', launch_markets: approveMarkets })
              setApprovePrompt(null)
              const made = (upd && upd._marketsCreated) || []
              const had  = (upd && upd._marketsExisting) || []
              let msg = 'Approved'
              if (made.length) msg += ` — ${made.length} market(s) created: ${made.join(', ')}`
              if (had.length) msg += made.length ? ` · ${had.length} already existed` : ` — all ${had.length} market(s) already existed, nothing changed`
              toast.success(msg)
              softLoad()
            }
            catch(err){ toast.error(err.message) }
          }}>Approve & Create {approveMarkets.length} Market{approveMarkets.length!==1?'s':''}</button></>}>
        <div style={{fontSize:13,color:'var(--text2)',marginBottom:14}}>Select which markets to launch. Rows will be created in Funnel Building, Creatives, Ad Copy, and Asset Prep for the checked markets only.</div>
        <div style={{display:'flex',flexWrap:'wrap',gap:10}}>
          {activeMarkets.map(m => {
            const checked = approveMarkets.includes(m)
            return <label key={m} onClick={() => setApproveMarkets(prev => prev.includes(m) ? prev.filter(x=>x!==m) : [...prev,m])}
              style={{display:'flex',alignItems:'center',gap:8,padding:'8px 14px',borderRadius:8,border:'1px solid '+(checked?'var(--accent)':'var(--border2)'),background:checked?'rgba(59,130,246,0.1)':'var(--bg3)',cursor:'pointer',fontSize:13,fontWeight:600,userSelect:'none'}}>
              <span style={{width:16,height:16,borderRadius:4,border:'1px solid '+(checked?'var(--accent)':'var(--border2)'),background:checked?'var(--accent)':'transparent',color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11}}>{checked?'✓':''}</span>
              {m}
            </label>
          })}
        </div>
        <div style={{marginTop:14,display:'flex',gap:8}}>
          <button className="btn btn-secondary" style={{fontSize:11,padding:'4px 10px'}} onClick={()=>setApproveMarkets([...activeMarkets])}>Select All</button>
          <button className="btn btn-secondary" style={{fontSize:11,padding:'4px 10px'}} onClick={()=>setApproveMarkets([])}>Clear All</button>
        </div>
      </Modal>}
      {modal && (
        <Modal title={modal==='add'?'Add New Product':`Edit: ${modal.product_name}`} onClose={()=>setModal(null)}
          footer={<>
            <button className="btn btn-ghost" onClick={()=>setModal(null)}>Cancel</button>
            <button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Saving...':'Save'}</button>
          </>}>
          <div className="form-row">
            <Input label="Product ID" type="number" value={form.product_id||''} onChange={set('product_id')} />
            <Input label="USD → EUR Rate" type="number" step="0.0001" value={form.usd_eur_rate||0.93} onChange={set('usd_eur_rate')} />
          </div>
          <Input label="Comp Product Name" value={form.product_name||''} onChange={set('product_name')} />
          <div className="form-row">
            <Input label="Sourcing/Box Cost (USD)" type="number" step="0.01" value={form.sourcing_cost_usd||''} onChange={set('sourcing_cost_usd')} />
            <Input label="Shipping Cost (USD)" type="number" step="0.01" value={form.shipping_cost_usd||''} onChange={set('shipping_cost_usd')} />
          </div>
          <Input label="Target Selling Price (€)" type="number" step="0.01" value={form.selling_price_eur||''} onChange={set('selling_price_eur')} />
          <Input label="Sourcing Link" type="url" value={form.aliexpress_link||''} onChange={set('aliexpress_link')} />
          <div className="form-row">
            <Input label="Competitor Product URL" type="url" value={form.competitor_product_url||''} onChange={set('competitor_product_url')} />
            <Input label="Competitor Advertorial URL" type="url" value={form.competitor_advertorial_url||''} onChange={set('competitor_advertorial_url')} />
            <Input label="Competitor Page Ad URL" type="url" value={form.competitor_page_ad_url||''} onChange={set('competitor_page_ad_url')} />
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12}}>
            <HtmlField label="Comp Advertorial HTML" hasExisting={!!(modal && modal.has_adv_html)} onChange={v=>setForm(f=>({...f,comp_adv_html:v}))} />
            <HtmlField label="Comp Sales Page HTML" hasExisting={!!(modal && modal.has_sales_html)} onChange={v=>setForm(f=>({...f,comp_sales_html:v}))} />
          </div>
          <Textarea label="Competitor Winning Ad Links (one per line)" value={form.competitor_winning_ad_links||''} onChange={set('competitor_winning_ad_links')} />
          <div className="form-row">
            <Textarea label="Competitor Ad Copy — Primary Text" value={form.competitor_ad_copy_primary||''} onChange={set('competitor_ad_copy_primary')} />
            <Textarea label="Competitor Ad Copy — Headline" value={form.competitor_ad_copy_headline||''} onChange={set('competitor_ad_copy_headline')} />
          </div>
          <Input label="Competitor Ad Creative Link (Drive)" type="url" value={form.competitor_ad_creative_link||''} onChange={set('competitor_ad_creative_link')} />
          <div style={{border:'1px solid var(--border)',borderRadius:'var(--radius)',marginBottom:16,background:'var(--surface)'}}>
            <div onClick={()=>setOffersOpen(o=>!o)} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'11px 14px',cursor:'pointer'}}>
              <span style={{fontSize:13,fontWeight:600,color:'var(--text)'}}>Per-Market Offers {(() => { const o=form.offer_by_market||{}; const c=Object.values(o).filter(v=>v&&v.trim()).length; return c?`(${c} set)`:'' })()}</span>
              <span style={{fontSize:12,color:'var(--text2)'}}>{offersOpen?'▲':'▼'}</span>
            </div>
            {offersOpen && <div style={{padding:'4px 14px 14px',display:'flex',flexDirection:'column',gap:10,borderTop:'1px solid var(--border)'}}>
              {activeMarkets.map(m => (
                <div key={m}>
                  <label style={{fontSize:11,fontWeight:600,color:'var(--text2)',display:'block',marginBottom:4,textTransform:'uppercase',letterSpacing:'0.5px'}}>{m}</label>
                  <input
                    placeholder="e.g. 1x=39.99, 2x=49.99, 3x=59.39"
                    value={(form.offer_by_market||{})[m]||''}
                    onChange={e => setForm(f => ({...f, offer_by_market: {...(f.offer_by_market||{}), [m]: e.target.value}}))} />
                </div>
              ))}
            </div>}
          </div>
          <Textarea label="Your Recommendations" value={form.research_notes||''} onChange={set('research_notes')} />
          <Input label="Platform Found On" value={form.platform_found_on||''} onChange={set('platform_found_on')} />
          <Input label="Competitor Price" value={form.competitor_price||''} onChange={set('competitor_price')} />
          <Input label="Ad Running Duration" value={form.ad_running_duration||''} onChange={set('ad_running_duration')} />
          <Input label="Countries Running In" value={form.ad_countries||''} onChange={set('ad_countries')} />
          <Input label="Scale Signal" value={form.scale_signal||''} onChange={set('scale_signal')} />
          <Textarea label="Ad Hook" value={form.ad_hook||''} onChange={set('ad_hook')} />
          <Textarea label="Angle Used" value={form.ad_angle||''} onChange={set('ad_angle')} />
          <Textarea label="Market Saturated?" value={form.market_saturated||''} onChange={set('market_saturated')} />
          <Textarea label="Desire Targeted" value={form.desire_targeted||''} onChange={set('desire_targeted')} />
          <Textarea label="Customer Concerns" value={form.customer_concerns||''} onChange={set('customer_concerns')} />
          <Textarea label="Problem Addressed" value={form.problem_addressed||''} onChange={set('problem_addressed')} />
          <Textarea label="Target Audience" value={form.target_audience||''} onChange={set('target_audience')} />
          <Textarea label="Key Competitor Positioning" value={form.competitor_positioning||''} onChange={set('competitor_positioning')} />
        </Modal>
      )}
      {generatorProduct && (
        <ContentGenerator
          product={generatorProduct}
          onClose={() => setGeneratorProduct(null)}
        />
      )}
    </div>
  )
}

export default ProductResearch
